import React, { useEffect, useRef, useState } from "react"
import {
  ShoppingCart,
  User,
  Menu,
  X,
  ChevronDown,
  LogOut,
  Search,
  KeyRound,
} from "lucide-react"
import { Menu as AccountMenu, MenuItem } from "@mui/material"
import { ChangePasswordDialog } from "./ChangePasswordDialog"
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom"
import { useCategories } from "@/hooks/useCategories"
import { useCart } from "@/hooks/useCart"
import { useLogout } from "@/hooks/useLogout"
import { useWebsiteConfig } from "@/hooks/useWebsiteConfig"

export const Navbar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [accountAnchor, setAccountAnchor] = useState<HTMLElement | null>(null)
  const [isPasswordDialogOpen, setIsPasswordDialogOpen] = useState(false)
  const [expandedCategoryId, setExpandedCategoryId] = useState<number | null>(
    null
  )
  const [searchTerm, setSearchTerm] = useState<string>("")
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const location = useLocation()
  const debounceTimer = useRef<number | null>(null)

  useEffect(() => {
    setSearchTerm(searchParams.get("search") ?? "")
  }, [searchParams])

  const { data: categoriesData, isLoading: categoriesLoading } = useCategories()
  const { cart } = useCart()
  const logoutMutation = useLogout()
  const { data: websiteConfig } = useWebsiteConfig()
  const token = localStorage.getItem("website_token")
  const customerStr = localStorage.getItem("customer")
  const customer = customerStr ? JSON.parse(customerStr) : null
  const initials = customer?.name
    ?.trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part: string) => Array.from(part)[0])
    .join("")
    .toUpperCase()

  // ApiResponse wraps in 'data', and ResourceCollection also wraps in 'data'
  // So for collections: response.data.data
  const categories =
    (categoriesData as any)?.data?.data || (categoriesData as any)?.data || []
  const cartItemCount = cart?.items?.length || 0

  const handleLogout = () => {
    setAccountAnchor(null)
    logoutMutation.mutate()
  }

  const updateSearchParam = (value: string) => {
    const params = new URLSearchParams(searchParams)

    if (value.trim()) {
      params.set("search", value.trim())
    } else {
      params.delete("search")
    }

    navigate({
      pathname: location.pathname,
      search: params.toString() ? `?${params.toString()}` : "",
    })
  }

  const handleSearchTermChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value
    setSearchTerm(value)

    if (debounceTimer.current) {
      window.clearTimeout(debounceTimer.current)
    }

    debounceTimer.current = window.setTimeout(() => {
      updateSearchParam(value)
    }, 300)
  }

  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        window.clearTimeout(debounceTimer.current)
      }
    }
  }, [])

  const handleSearchSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    updateSearchParam(searchTerm)
  }

  return (
    <nav className="bg-white shadow-sm sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16 items-center">
          {/* Logo */}
          <div className="flex-shrink-0 flex items-center">
            <Link
              to="/"
              className="text-2xl font-bold text-indigo-600 flex items-center h-full"
            >
              {websiteConfig?.logo_image ? (
                <img
                  src={websiteConfig.logo_image}
                  alt={websiteConfig?.title || "Logo"}
                  className="max-h-12 w-auto object-contain"
                />
              ) : (
                "SHOP.CO"
              )}
            </Link>
          </div>

          {/* Desktop Menu with Hover Dropdown */}
          <div className="hidden md:flex space-x-8 items-center h-full">
            {categoriesLoading ? (
              <div className="text-gray-500">Loading...</div>
            ) : (
              categories.map((category: any) => {
                const hasTags = category.tags?.length > 0

                return (
                  <div
                    key={category.id}
                    className="relative group h-full flex items-center"
                  >
                    {hasTags ? (
                      <button
                        type="button"
                        aria-label={`Show ${category.name} tags`}
                        className="flex items-center text-gray-700 hover:text-indigo-600 font-medium transition-colors"
                      >
                        {category.name}
                        <ChevronDown size={16} className="ml-1" />
                      </button>
                    ) : (
                      <span className="font-medium text-gray-700">
                        {category.name}
                      </span>
                    )}

                    {hasTags && (
                      <div className="absolute top-full left-0 w-48 bg-white shadow-lg rounded-b-md py-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible group-focus-within:opacity-100 group-focus-within:visible transition-all duration-200 border-t-2 border-indigo-600 transform translate-y-2 group-hover:translate-y-0 group-focus-within:translate-y-0">
                        {category.tags.map((tag: any) => (
                          <Link
                            key={tag.id}
                            to={`/tag/${tag.id}?category_id=${category.id}`}
                            className="block px-4 py-2 text-sm text-gray-700 hover:bg-indigo-50 hover:text-indigo-600"
                          >
                            {tag.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                )
              })
            )}
          </div>

          {/* Icons */}
          <div className="flex items-center space-x-3 sm:space-x-6">
            <form
              onSubmit={handleSearchSubmit}
              className="hidden sm:flex items-center space-x-2"
            >
              <input
                type="text"
                value={searchTerm}
                onChange={handleSearchTermChange}
                placeholder="Search products"
                aria-label="Search products"
                className="rounded-full border border-gray-200 bg-gray-100 px-3 py-1 text-sm text-gray-700 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
              />
              <button
                type="submit"
                className="text-gray-600 hover:text-indigo-600"
                aria-label="Submit search"
              >
                <Search className="w-6 h-6" />
              </button>
            </form>
            <Link to="/cart" className="relative">
              <ShoppingCart className="w-6 h-6 text-gray-600 hover:text-indigo-600" />
              {cartItemCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                  {cartItemCount}
                </span>
              )}
            </Link>
            {token ? (
              <>
                <button
                  type="button"
                  aria-label="Open account menu"
                  aria-haspopup="menu"
                  aria-expanded={!!accountAnchor}
                  aria-controls={
                    accountAnchor ? "customer-account-menu" : undefined
                  }
                  onClick={(event) => setAccountAnchor(event.currentTarget)}
                  className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-indigo-100 text-sm font-semibold text-indigo-700 hover:bg-indigo-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-indigo-600"
                >
                  {initials || <User size={20} />}
                </button>
                <AccountMenu
                  id="customer-account-menu"
                  anchorEl={accountAnchor}
                  open={!!accountAnchor}
                  onClose={() => setAccountAnchor(null)}
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  transformOrigin={{ vertical: "top", horizontal: "right" }}
                  PaperProps={{
                    sx: { minWidth: 220, maxWidth: "calc(100vw - 32px)" },
                  }}
                >
                  <div className="border-b border-gray-100 px-4 py-3">
                    <p className="break-words text-sm font-semibold text-gray-900">
                      {customer?.name || "My Account"}
                    </p>
                  </div>
                  <MenuItem
                    component={Link}
                    to="/orders"
                    onClick={() => setAccountAnchor(null)}
                  >
                    <ShoppingCart size={18} className="mr-2" />
                    Orders
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      setAccountAnchor(null)
                      setIsPasswordDialogOpen(true)
                    }}
                  >
                    <KeyRound size={18} className="mr-2" />
                    Change Password
                  </MenuItem>
                  <MenuItem
                    onClick={handleLogout}
                    disabled={logoutMutation.isPending}
                  >
                    <LogOut size={18} className="mr-2" />
                    Logout
                  </MenuItem>
                </AccountMenu>
                <ChangePasswordDialog
                  open={isPasswordDialogOpen}
                  onClose={() => setIsPasswordDialogOpen(false)}
                />
              </>
            ) : (
              <Link to="/login">
                <User className="w-6 h-6 text-gray-600 hover:text-indigo-600" />
              </Link>
            )}

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="md:hidden text-gray-600"
            >
              {isOpen ? <X /> : <Menu />}
            </button>
          </div>
        </div>
        <form
          onSubmit={handleSearchSubmit}
          className="flex sm:hidden items-center gap-2 pb-3"
        >
          <input
            type="text"
            value={searchTerm}
            onChange={handleSearchTermChange}
            placeholder="Search products"
            aria-label="Search products"
            className="min-w-0 flex-1 rounded-full border border-gray-200 bg-gray-100 px-3 py-2 text-sm text-gray-700 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
          />
          <button
            type="submit"
            aria-label="Submit search"
            className="p-2 text-gray-600 hover:text-indigo-600"
          >
            <Search size={22} />
          </button>
        </form>
      </div>

      {/* Mobile Menu (Simple implementation) */}
      {isOpen && (
        <div className="md:hidden bg-white border-t">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
            {categories.map((category: any) => {
              const hasTags = category.tags?.length > 0

              return (
                <div key={category.id}>
                  {hasTags ? (
                    <button
                      type="button"
                      onClick={() =>
                        setExpandedCategoryId((current) =>
                          current === category.id ? null : category.id
                        )
                      }
                      aria-expanded={expandedCategoryId === category.id}
                      className="flex w-full items-center justify-between px-3 py-2 text-base font-medium text-gray-700 hover:bg-gray-50"
                    >
                      {category.name}
                      <ChevronDown
                        size={16}
                        className={
                          expandedCategoryId === category.id ? "rotate-180" : ""
                        }
                      />
                    </button>
                  ) : (
                    <span className="block px-3 py-2 text-base font-medium text-gray-700">
                      {category.name}
                    </span>
                  )}
                  {hasTags && expandedCategoryId === category.id && (
                    <div className="pl-6">
                      {category.tags.map((tag: any) => (
                        <Link
                          key={tag.id}
                          to={`/tag/${tag.id}?category_id=${category.id}`}
                          onClick={() => setIsOpen(false)}
                          className="block px-3 py-2 text-sm text-gray-600 hover:bg-gray-50"
                        >
                          {tag.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}
    </nav>
  )
}
